import Form from '../form'
import FormImageInput from '../form-image-input'
import FormInput from '../form-input'
import FormSwitch from '../form-switch'
import React, { ReactElement, useState } from 'react'
import TimezoneSelector, { defaultTimezone } from '../timezone-selector'
import VendorRegionSelect from '@shared/vendor-region-select'
import dayjs from 'dayjs'
import useAllActions from '@hooks/use-all-actions'
import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material'
import { OperatingState, Store } from '@types'
import { TimePicker } from '@mui/x-date-pickers'
import { getErrorMessage } from '@utils/string-utils'
import { makeStyles } from 'tss-react/mui'

const operatingStateLabels: Record<OperatingState, string> = {
  [OperatingState.hidden]: 'Hidden',
  [OperatingState.visible]: 'Visible',
}

const StoreForm = ({
  canEdit,
  canEditRestrictedSettings,
  canManageOfflineRestriction,
  className,
  onSubmit,
  store: initialStore,
  title,
}: Props): ReactElement => {
  const { classes } = useStyles()
  const { setFlashMessage } = useAllActions()

  const isEditable = canEdit !== false
  const store: InitialStoreData = initialStore || {}

  const [imageUrl, setImageUrl] = useState(store.image_url)
  const [imageName, setImageName] = useState('')
  const [headerImageOneUrl, setHeaderImageOneUrl] = useState(
    store.header_image_one_url,
  )
  const [headerImageOneName, setHeaderImageOneName] = useState('')
  const [headerImageTwoUrl, setHeaderImageTwoUrl] = useState(
    store.header_image_two_url,
  )
  const [headerImageTwoName, setHeaderImageTwoName] = useState('')
  const [name, setName] = useState(store.name || '')
  const [netsuiteId, setNetsuiteId] = useState(store.netsuite_id || '')
  const [state, setState] = useState(store.state || '')
  const [city, setCity] = useState(store.city || '')
  const [postalCode, setPostalCode] = useState(store.postal_code || '')
  const [address, setAddress] = useState(store.address || '')
  const [region, setRegion] = useState(store.region || '')
  const [area, setArea] = useState(store.area || '')
  const [hasDriveThru, setHasDriveThru] = useState(
    store.has_drive_thru || false,
  )
  // Edited through number fields, so these hold numbers once the user types.
  const [latitude, setLatitude] = useState<number | string>(
    store.latitude || '',
  )
  const [longitude, setLongitude] = useState<number | string>(
    store.longitude || '',
  )
  const [timeZone, setTimeZone] = useState(store.time_zone || defaultTimezone)
  const [phone, setPhone] = useState(store.phone || '')
  const [email, setEmail] = useState(store.email_address)
  const [maxPrepTime, setMaxPrepTime] = useState(store.max_prep_time || 150)
  const [bdaPassword, setBdaPassword] = useState(store.bda_password || '')
  const [minutesToClosingWarning, setMinutesToClosingWarning] = useState(
    store.minutes_to_closing_warning || 10,
  )
  const [acceptsMobileOrders, setAcceptsMobileOrders] = useState(
    store.accepts_mobile_orders == null ? true : store.accepts_mobile_orders,
  )
  const [yextId, setYextId] = useState(store.yext_id || '')
  const [doordashId, setDoordashId] = useState(store.doordash_id || '')
  const [paytronixId, setPaytronixId] = useState(store.paytronix_id || '')
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(
    store.loyalty_enabled || false,
  )
  const [description, setDescription] = useState(store.description || '')
  const [vendorRegionId, setVendorRegionId] = useState(
    store.vendor_region_id || null,
  )
  const [operatingState, setOperatingState] = useState<OperatingState>(
    store.operating_state || OperatingState.hidden,
  )
  const [restrictOfflineAccess, setRestrictOfflineAccess] = useState(
    store.restrict_offline_access || false,
  )
  const [afternoonAt, setAfternoonAt] = useState(store.afternoon_at || '')
  const [eveningAt, setEveningAt] = useState(store.evening_at || '')

  const getBase64ImageFromUrl = async (url) => {
    // Get the base 64 image data
    return new Promise((resolve) => {
      fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
          const reader = new FileReader()
          reader.readAsDataURL(blob)
          reader.onloadend = () => {
            const base64Data = reader.result
            resolve(base64Data)
          }
        })
    })
  }

  const getImage = async () => {
    // No image URL => no Image
    if (imageUrl == null || imageUrl === store.image_url) return null
    return getBase64ImageFromUrl(imageUrl)
  }

  const getHeaderImageOne = async () => {
    if (
      headerImageOneUrl == null ||
      headerImageOneUrl === store.header_image_one_url
    )
      return null
    return getBase64ImageFromUrl(headerImageOneUrl)
  }

  const getHeaderImageTwo = async () => {
    if (
      headerImageTwoUrl == null ||
      headerImageTwoUrl === store.header_image_two_url
    )
      return null
    return getBase64ImageFromUrl(headerImageTwoUrl)
  }

  const getContentfulId = () => {
    return Math.random().toString(36).substr(2, 9)
  }

  const getStore = async (): Promise<StoreFormData> => {
    if (!name) throw new Error('Must enter a name.')
    if (!netsuiteId) throw new Error('Must enter a Netsuite ID.')

    const data: StoreFormData = {
      name,
      netsuite_id: netsuiteId,
      contentful_id: getContentfulId(),
      phone,
      email_address: email,
      state,
      city,
      postal_code: postalCode,
      address,
      region,
      area,
      has_drive_thru: hasDriveThru,
      longitude,
      latitude,
      time_zone: timeZone,
      bda_password: bdaPassword,
      max_prep_time: maxPrepTime,
      minutes_to_closing_warning: minutesToClosingWarning,
      yext_id: yextId,
      doordash_id: doordashId,
      paytronix_id: paytronixId,
      vendor_region_id: vendorRegionId,
      description: description,
      afternoon_at: afternoonAt,
      evening_at: eveningAt,
    }

    // Restricted settings: gated on `stores:create` rather than the broader
    // `stores:update`. Omit them entirely when the user can't set them — a
    // PATCH without a field leaves it untouched.
    if (canEditRestrictedSettings) {
      data.operating_state = operatingState
      data.accepts_mobile_orders = acceptsMobileOrders
      data.loyalty_enabled = loyaltyEnabled
    }

    // The API rejects the whole PATCH when this field is sent by someone
    // without `stores:manage-offline-restriction`, so omit it rather than
    // losing every other edit in the form to a 403.
    if (canManageOfflineRestriction) {
      data.restrict_offline_access = restrictOfflineAccess
    }

    const image = await getImage()
    if (image != null && imageName != null) {
      data.store_image_data = image
      data.image_filename = imageName
    }
    const headerImageOne = await getHeaderImageOne()
    if (headerImageOne != null && headerImageOneName != null) {
      data.store_header_image_one_data = headerImageOne
      data.store_header_image_one_filename = headerImageOneName
    }
    const headerImageTwo = await getHeaderImageTwo()
    if (headerImageTwo != null && headerImageTwoName != null) {
      data.store_header_image_two_data = headerImageTwo
      data.store_header_image_two_filename = headerImageTwoName
    }

    return data
  }

  const handleDayPartChange = (onChange) => (newDate) => {
    onChange(newDate.format('HH:mm'))
  }

  const handleImageChange = (data) => {
    setImageName(data.name)
    setImageUrl(data.url)
  }

  const handleHeaderImageOneChange = (data) => {
    setHeaderImageOneName(data.name)
    setHeaderImageOneUrl(data.url)
  }

  const handleHeaderImageTwoChange = (data) => {
    setHeaderImageTwoName(data.name)
    setHeaderImageTwoUrl(data.url)
  }

  const handleOperatingStateChange = (
    event: SelectChangeEvent<OperatingState>,
  ) => setOperatingState(event.target.value as OperatingState)

  const handleLatitudeChange = (text: string) => setLatitude(parseFloat(text))

  const handleLongitudeChange = (text: string) => setLongitude(parseFloat(text))

  const handleMaxPrepTimeChange = (text: string) =>
    setMaxPrepTime(parseInt(text, 10))

  const handleMinutesToClosingChange = (text: string) =>
    setMinutesToClosingWarning(parseInt(text, 10))

  const handleSubmit = async () => {
    try {
      const updatedStore = await getStore()
      if (updatedStore != null) onSubmit(updatedStore)
    } catch (error) {
      setFlashMessage(getErrorMessage(error), 'error')
    }
  }

  return (
    <Form className={className} title={title}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <FormImageInput
            imageUrl={imageUrl}
            isEditing={isEditable}
            onChange={handleImageChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={4}>
            <Grid item lg={4} xs={6}>
              <FormControl className={classes.operatingState} fullWidth>
                <InputLabel id='operating-state-label'>
                  Operating State
                </InputLabel>
                <Select
                  disabled={!isEditable || !canEditRestrictedSettings}
                  labelId='operating-state-label'
                  onChange={handleOperatingStateChange}
                  value={operatingState}
                >
                  {Object.values(OperatingState).map((option) => (
                    <MenuItem key={option} value={option}>
                      {operatingStateLabels[option]}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Hidden stores are not shown to customers.
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item lg={4} xs={6}>
              <FormSwitch
                checked={restrictOfflineAccess}
                disabled={!isEditable || !canManageOfflineRestriction}
                helperText='When on, only authorized users can take this store offline or bring it back online. The BDA is blocked entirely.'
                onChange={setRestrictOfflineAccess}
                title='Restrict Offline Access'
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            onChangeText={setName}
            title='Name'
            value={name}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            inputProps={{ maxLength: 255 }}
            onChangeText={setDescription}
            title='Description'
            value={description}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            helperText='This is not the Netsuite store number but the internal Netsuite ID'
            onChangeText={setNetsuiteId}
            title='Netsuite ID'
            value={netsuiteId}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            onChangeText={setState}
            title='State'
            value={state}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            onChangeText={setCity}
            title='City'
            value={city}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            onChangeText={setPostalCode}
            title='Postal Code'
            value={postalCode}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            onChangeText={setAddress}
            title='Address'
            value={address}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            helperText='Region the customer selects to find the store'
            onChangeText={setRegion}
            title='Region'
            value={region}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <VendorRegionSelect
            className={classes.vendorRegionSelect}
            disabled={!isEditable}
            onChange={setVendorRegionId}
            value={vendorRegionId as number}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            onChangeText={setArea}
            title='Area'
            value={area}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            inputProps={{ type: 'number' }}
            onChangeText={handleLatitudeChange}
            title='Latitude'
            value={latitude}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            inputProps={{ type: 'number' }}
            onChangeText={handleLongitudeChange}
            title='Longitude'
            value={longitude}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <TimezoneSelector
            className={classes.timeZone}
            disabled={!isEditable}
            onChange={setTimeZone}
            value={timeZone}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            onChangeText={setPhone}
            title='Phone'
            value={phone}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            onChangeText={setEmail}
            title='Email'
            value={email}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            onChangeText={setBdaPassword}
            title='BDA Password'
            value={bdaPassword}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            inputProps={{ type: 'number' }}
            onChangeText={handleMaxPrepTimeChange}
            title='Max Prep Time'
            value={maxPrepTime.toString()}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            inputProps={{ type: 'number' }}
            onChangeText={handleMinutesToClosingChange}
            title='Minutes to Closing Warning'
            value={minutesToClosingWarning.toString()}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormSwitch
            checked={hasDriveThru}
            disabled={!isEditable}
            helperText='Set whether or not this store has a drive thru.'
            onChange={setHasDriveThru}
            title='Has a Drive Thru'
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormSwitch
            checked={acceptsMobileOrders}
            disabled={!isEditable || !canEditRestrictedSettings}
            helperText='Set whether or not this store accepts mobile orders.'
            onChange={setAcceptsMobileOrders}
            title='Accepts Mobile Orders'
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            inputProps={{ type: 'string' }}
            onChangeText={setYextId}
            title='Yext Entity ID'
            value={yextId}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            inputProps={{ type: 'string' }}
            onChangeText={setDoordashId}
            title='Doordash Store ID'
            value={doordashId}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormInput
            disabled={!isEditable}
            inputProps={{ type: 'string' }}
            onChangeText={setPaytronixId}
            title='Paytronix Store ID'
            value={paytronixId}
          />
        </Grid>
        <Grid item lg={4} xs={6}>
          <FormSwitch
            checked={loyaltyEnabled}
            disabled={!isEditable || !canEditRestrictedSettings}
            helperText='Set whether or not this store is included in the loyalty program.'
            onChange={setLoyaltyEnabled}
            title='Loyalty Enabled'
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={4}>
            <Grid item xs={4}>
              <FormControl>
                <InputLabel>Morning Begins At</InputLabel>
                <Select disabled value='1'>
                  <MenuItem key={0} value='1'>
                    Store Opening
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TimePicker
                disabled={!isEditable}
                label='Afternoon Begins At'
                onChange={handleDayPartChange(setAfternoonAt)}
                value={
                  afternoonAt == '' ? null : dayjs('0000-01-01 ' + afternoonAt)
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TimePicker
                disabled={!isEditable}
                label='Evening Begins At'
                onChange={handleDayPartChange(setEveningAt)}
                value={
                  eveningAt == '' ? null : dayjs('0000-01-01 ' + eveningAt)
                }
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <span>Header Image One</span>
          <FormImageInput
            imageUrl={headerImageOneUrl}
            isEditing={isEditable}
            onChange={handleHeaderImageOneChange}
          />
        </Grid>
        <Grid item xs={12}>
          <span>Header Image Two</span>
          <FormImageInput
            imageUrl={headerImageTwoUrl}
            isEditing={isEditable}
            onChange={handleHeaderImageTwoChange}
          />
        </Grid>
      </Grid>
      {isEditable && (
        <Button
          className={classes.submitButton}
          color='secondary'
          onClick={handleSubmit}
          variant='contained'
        >
          Submit
        </Button>
      )}
    </Form>
  )
}

export default StoreForm

/**
 * The subset of a store the form knows how to edit. Not a `Store` — it carries
 * the uploaded image payloads and omits server-owned fields.
 */
export type StoreFormData = {
  name: string
  netsuite_id: string
  contentful_id: string
  phone: string
  email_address?: string
  state: string
  city: string
  postal_code: string
  address: string
  region: string
  area: string
  has_drive_thru: boolean
  longitude: number | string
  latitude: number | string
  time_zone: string
  bda_password: string
  max_prep_time: number
  minutes_to_closing_warning: number
  accepts_mobile_orders?: boolean
  yext_id?: string
  doordash_id?: string
  paytronix_id?: string
  loyalty_enabled?: boolean
  vendor_region_id?: number | null
  description?: string
  operating_state?: OperatingState
  restrict_offline_access?: boolean
  afternoon_at?: string
  evening_at?: string
  store_image_data?: any
  image_filename?: string
  store_header_image_one_data?: any
  store_header_image_one_filename?: string
  store_header_image_two_data?: any
  store_header_image_two_filename?: string
}

/**
 * These fields aren't on `Store` yet, and every field is optional so the form
 * can also render for a brand new store.
 */
type InitialStoreData = Partial<Store> & {
  header_image_one_url?: string
  header_image_two_url?: string
  yext_id?: string
  doordash_id?: string
  paytronix_id?: string
  loyalty_enabled?: boolean
  description?: string
  vendor_region_id?: number | null
  afternoon_at?: string
  evening_at?: string
}

type Props = {
  canEdit?: boolean
  canEditRestrictedSettings?: boolean
  canManageOfflineRestriction?: boolean
  className?: string
  onSubmit: (store: StoreFormData) => void | Promise<void>
  store?: InitialStoreData
  title?: string
}

const useStyles = makeStyles()((theme) => ({
  submitButton: {
    marginTop: theme.spacing(3),
    color: 'white',
  },
  vendorRegionSelect: {
    width: '100%',
    marginBottom: '16px',
  },
  timeZone: {
    marginBottom: '16px',
  },
  operatingState: {
    marginBottom: '16px',
  },
}))
